
import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Building2, Users, Briefcase, Handshake } from "lucide-react";
import RelatedPartyTransactions from "./RelatedPartyTransactions";

// Types for entities
type EntityBase = {
  legal_name: string;
  share_holding_percentage: number;
  city?: string;
  incorporation_date?: string;
  status?: string;
};

type Company = EntityBase & {
  cin: string;
  paid_up_capital: number;
  sum_of_charges: number;
  active_compliance: string | null;
  cirp_status: string | null;
  next_cin: string | null;
};

type LLP = EntityBase & {
  llpin: string;
  total_obligation_of_contribution: number;
  sum_of_charges: number;
  cirp_status: string | null;
};

type Other = EntityBase;

type EntityWithType = EntityBase & {
  entityType: string;
  cin?: string;
  llpin?: string;
};

type EntityGroup = {
  financial_year: string;
  company: Company[];
  llp: LLP[];
  others: Other[];
};

type RelatedPartyProps = {
  holdingEntities: EntityGroup | null;
  subsidiaryEntities: EntityGroup | null;
  associateEntities: EntityGroup | null;
  jointVentures: EntityGroup | null;
  relatedPartyTransactions: any[] | null;
};

// Format date function
const formatDate = (dateString?: string) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Enhanced Entity table component
const EntityTable = ({ entities }: { entities: EntityWithType[] }) => {
  if (entities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No entities found
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="font-semibold">Corporate Name</TableHead>
            <TableHead className="font-semibold text-center">Type</TableHead>
            <TableHead className="font-semibold text-center">Shareholding (%)</TableHead>
            <TableHead className="font-semibold">City</TableHead>
            <TableHead className="font-semibold">DOI</TableHead>
            <TableHead className="font-semibold text-center">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entities.map((entity, index) => (
            <TableRow key={index} className="hover:bg-gray-50 transition-colors">
              <TableCell className="font-medium">
                <div>
                  <div className="text-gray-900">{entity.legal_name}</div>
                  {entity.cin && (
                    <div className="text-xs text-gray-500">CIN: {entity.cin}</div>
                  )}
                  {entity.llpin && (
                    <div className="text-xs text-gray-500">LLPIN: {entity.llpin}</div>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-center">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {entity.entityType}
                </Badge>
              </TableCell>
              <TableCell className="text-center font-medium">
                {entity.share_holding_percentage}%
              </TableCell>
              <TableCell>{entity.city || "—"}</TableCell>
              <TableCell>{formatDate(entity.incorporation_date)}</TableCell>
              <TableCell className="text-center">
                <Badge 
                  variant={entity.status === "Active" ? "default" : "secondary"}
                  className={entity.status === "Active" ? "bg-green-100 text-green-700" : ""}
                >
                  {entity.status || "—"}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// Enhanced EntityGroup accordion component
const EntityGroupAccordion = ({
  title,
  entityGroup,
  icon: Icon,
}: {
  title: string;
  entityGroup: EntityGroup;
  icon: React.ComponentType<{ className?: string }>;
}) => {
  const companies = entityGroup?.company || [];
  const llps = entityGroup?.llp || [];
  const others = entityGroup?.others || [];
  
  const totalCount = companies.length + llps.length + others.length;
  const hasAnyData = totalCount > 0;

  if (!hasAnyData) {
    return (
      <AccordionItem value={title.toLowerCase().replace(/\s+/g, "-")}>
        <AccordionTrigger className="hover:bg-gray-50 px-4 py-3 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5 text-blue-600" />
            <span className="font-medium">{title}</span>
            {/* <Badge variant="secondary" className="ml-auto">0</Badge> */}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <p className="text-gray-500 text-center py-8">
            No data available for {title}
          </p>
        </AccordionContent>
      </AccordionItem>
    );
  }

  // Combine all entities into a single table with proper typing
  const allEntities: EntityWithType[] = [
    ...companies.map(entity => ({ ...entity, entityType: 'Company' })),
    ...llps.map(entity => ({ ...entity, entityType: 'LLP' })),
    ...others.map(entity => ({ ...entity, entityType: 'Others' }))
  ];

  return (
    <AccordionItem value={title.toLowerCase().replace(/\s+/g, "-")}>
      <AccordionTrigger className="hover:bg-gray-50 px-4 py-3 rounded-lg">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-blue-600" />
          <span className="font-medium">{title}</span>
          <div className="flex gap-2 ml-auto">
            {companies.length > 0 && (
              <Badge variant="outline" className="bg-green-50 text-green-700">
                Company: {companies.length}
              </Badge>
            )}
            {llps.length > 0 && (
              <Badge variant="outline" className="bg-purple-50 text-purple-700">
                LLP: {llps.length}
              </Badge>
            )}
            {others.length > 0 && (
              <Badge variant="outline" className="bg-orange-50 text-orange-700">
                Others: {others.length}
              </Badge>
            )}
            {/* <Badge variant="secondary">{totalCount}</Badge> */}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <EntityTable entities={allEntities} />
      </AccordionContent>
    </AccordionItem>
  );
};

const RelatedParty: React.FC<RelatedPartyProps> = ({
  holdingEntities,
  subsidiaryEntities,
  associateEntities,
  jointVentures,
  relatedPartyTransactions,
}) => {
  return (
    <>
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          Related Party Details
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full space-y-2">
          <EntityGroupAccordion
            title="Holding Corporates"
            entityGroup={holdingEntities}
            icon={Building2}
          />
          <EntityGroupAccordion
            title="Subsidiary Corporates"
            entityGroup={subsidiaryEntities}
            icon={Briefcase}
          />
          <EntityGroupAccordion
            title="Associate Corporates"
            entityGroup={associateEntities}
            icon={Users}
          />
          <EntityGroupAccordion
            title="Joint Ventures"
            entityGroup={jointVentures}
            icon={Handshake}
          />
        </Accordion>
      </CardContent>
    </Card>
      {/* Related Party Transactions Section */}
      <RelatedPartyTransactions transactions={relatedPartyTransactions || []} />
    </>
    

    
  );
};

export default RelatedParty;
